import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleSkiVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "buy-ski-new-and-second-hand-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "where-to-buy-new-skis": "Where to Buy New Skis",
    "where-to-buy-used-skis": "Where to Buy Used Skis",
    "local-regulations": "Local Regulations & Best Practices",
    "pricing-guide": "Pricing Guide & Budgeting Tips",
    "gear-maintenance": "Ski Gear Maintenance & Longevity",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References"
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Tourism Vancouver"
    },
    {
      url: "https://www.skipatrol.ca/",
      label: "Canadian Ski Patrol"
    },
    {
      url: "https://www.mec.ca/",
      label: "Mountain Equipment Co-op (MEC)"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // *Each location's id must match the ref we use in the corresponding <p> or <li>.
  // *Coordinates from the final content.
  const locations = [
    // NEW SKIS LOCATIONS
    {
      id: "comor-kitsilano",
      lat: 49.26870,
      lng: -123.15350,
      popupText: "Comor - Kitsilano"
    },
    {
      id: "nssb",
      lat: 49.32450,
      lng: -123.07200,
      popupText: "North Shore Ski & Board (NSSB)"
    },
    {
      id: "skiis-biikes",
      lat: 49.26400,
      lng: -123.13100,
      popupText: "Skiis & Biikes (Vancouver)"
    },
    {
      id: "west-coast-sports",
      lat: 49.26495,
      lng: -123.21850,
      popupText: "West Coast Sports"
    },
    {
      id: "mec-vancouver",
      lat: 49.26951,
      lng: -123.10197,
      popupText: "MEC Vancouver"
    },

    // USED SKIS LOCATIONS
    {
      id: "sports-junkies",
      lat: 49.26383,
      lng: -123.10893,
      popupText: "Sports Junkies"
    },
    {
      id: "value-sports",
      lat: 49.28450,
      lng: -123.11235,
      popupText: "Value Sports"
    },
    {
      id: "swap-west-van-arena",
      lat: 49.33968,
      lng: -123.15530,
      popupText: "Ski Swaps & Community Sales (Example: West Van Arena)"
    }
    // Additional markers for Facebook Marketplace, etc., are not exact, so we omit
    // real coords for those since it's city-wide / variable.
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Buy New & Used Skis in Vancouver (Insider Tips!)</title>
        <meta
          name="description"
          content="Discover the best locations and deals for buying new or second-hand skis in Vancouver, including insider tips, local regulations, budgeting advice, and more."
        />
        <meta
          name="keywords"
          content="ski equipment vancouver, new skis, used skis, Vancouver ski shops"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/buy-ski-new-and-second-hand-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Buy New & Used Skis in Vancouver (Insider Tips!)"
        />
        <meta
          property="og:description"
          content="A complete guide to finding top deals on new and used skis in Vancouver, with location markers, regulations, FAQs, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/buy-ski-new-and-second-hand-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/buy-ski-new-and-second-hand-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Buy New & Used Skis in Vancouver (Insider Tips!)"
        />
        <meta
          name="twitter:description"
          content="A complete guide to finding top deals on new and used skis in Vancouver, with location markers, regulations, FAQs, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/buy-ski-new-and-second-hand-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Skiers in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver’s proximity to world-class resorts like Whistler
              Blackcomb and Cypress Mountain has fostered a thriving ski
              community. That means plenty of options when it comes to finding
              the perfect skis—both fresh out of the box and gently used. Whether
              you’re a seasoned powder hound or a first-timer on the slopes,
              knowing where to shop locally can save you money, time, and
              headaches. In this guide, you’ll discover the best places in
              Vancouver to buy new and second-hand skis, complete with insider
              tips on pricing, maintenance, local regulations, and more.
            </p>

            {/* SECTION: Where to Buy New Skis */}
            <h2 id="where-to-buy-new-skis" ref={(el) => setSpotRef("where-to-buy-new-skis", el)}>
              Where to Buy New Skis in Vancouver
            </h2>
            <p>
              Ready to invest in the latest gear? Vancouver is home to several
              top-notch ski shops that offer brand-new equipment, expert advice,
              and seasonal sales. Here are five popular spots:
            </p>

            <ol>
              <li>
                <strong>Comor – “Go Play Outside” (Kitsilano)</strong>
                <p
                  ref={(el) => setSpotRef("comor-kitsilano", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.26870}
                    coordX={-123.15350}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.26870, Longitude: -123.15350</em>
                </p>
                <p>
                  Located on West 4th Avenue, Comor offers an expansive selection
                  of high-performance skis, with brands like Atomic, Volkl, and
                  Salomon. The knowledgeable staff can help you find the perfect
                  all-mountain or powder ski to suit local conditions.
                  Expect prices ranging from about $400 for entry-level models
                  to $1,200+ for top-tier gear. Keep an eye out for end-of-season
                  sales and a price match guarantee.
                </p>
              </li>

              <li>
                <strong>North Shore Ski & Board (NSSB)</strong>
                <p
                  ref={(el) => setSpotRef("nssb", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.32450}
                    coordX={-123.07200}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.32450, Longitude: -123.07200</em>
                </p>
                <p>
                  Family-owned and conveniently located on Lonsdale Ave, NSSB
                  carries top brands (Rossignol, K2, Blizzard) and is known for
                  friendly service plus expert boot fitting. Prices range from
                  $500 for mid-range models to $900+ for premium skis.
                  Don’t miss their winter sales or the Junior Trade-In Program if
                  you have growing kids.
                </p>
              </li>

              <li>
                <strong>Skiis &amp; Biikes (Vancouver Store)</strong>
                <p
                  ref={(el) => setSpotRef("skiis-biikes", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.26400}
                    coordX={-123.13100}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.26400, Longitude: -123.13100</em>
                </p>
                <p>
                  If you want a large selection under one roof, Skiis & Biikes
                  has you covered. From beginner-friendly packages around $399 to
                  high-end expert skis over $1,000, there’s something for every
                  level. They also offer custom boot fitting and a kids’ buy-back
                  program, making it a one-stop shop for families.
                </p>
              </li>

              <li>
                <strong>West Coast Sports (Point Grey)</strong>
                <p
                  ref={(el) => setSpotRef("west-coast-sports", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.26495}
                    coordX={-123.21850}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.26495, Longitude: -123.21850</em>
                </p>
                <p>
                  Near UBC, West Coast Sports is a smaller, independent shop with
                  a curated selection of new skis (Nordica, Head, Armada). They
                  often have bundle deals and student specials, so if you’re on a
                  tighter budget or studying at UBC, it’s a perfect spot. Expect
                  to spend $500–$800 on an all-mountain setup, with occasional
                  sales in late season.
                </p>
              </li>

              <li>
                <strong>Mountain Equipment Co-op (MEC Vancouver)</strong>
                <p
                  ref={(el) => setSpotRef("mec-vancouver", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.26951}
                    coordX={-123.10197}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.26951, Longitude: -123.10197</em>
                </p>
                <p>
                  Best known for backcountry and touring gear, MEC carries brands
                  like G3, Dynafit, and Black Diamond, as well as resort skis.
                  Prices run from about $500 for basic models to over $1,000 for
                  premium setups. The co-op model means good value, plus a
                  generous return policy and member benefits.
                </p>
              </li>
            </ol>

            {/* SECTION: Where to Buy Used Skis */}
            <h2 id="where-to-buy-used-skis" ref={(el) => setSpotRef("where-to-buy-used-skis", el)}>
              Where to Buy Used Skis in Vancouver
            </h2>
            <p>
              Searching for a bargain on quality gear? Vancouver’s second-hand ski
              scene is robust, offering you a chance to save 30–60% off retail.
              Here are some top options:
            </p>
            <ol>
              <li>
                <strong>Sports Junkies</strong>
                <p
                  ref={(el) => setSpotRef("sports-junkies", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.26383}
                    coordX={-123.10893}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.26383, Longitude: -123.10893</em>
                </p>
                <p>
                  Dubbed Vancouver’s “used sports gear superstore,” Sports Junkies
                  on West Broadway features hundreds of used skis in various
                  conditions. Prices range from $100–$200 for older models to
                  $300–$500 for near-new sets. They also have a trade-in program,
                  making it simple to upgrade your gear over time.
                </p>
              </li>

              <li>
                <strong>Value Sports (Downtown)</strong>
                <p
                  ref={(el) => setSpotRef("value-sports", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.28450}
                    coordX={-123.11235}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.28450, Longitude: -123.11235</em>
                </p>
                <p>
                  This downtown spot prides itself on its massive used ski
                  selection, often with over 800 pairs in stock. Prices start
                  around $150, and you can find newer models in the $300–$400
                  range. They tune most skis before selling, so you can head
                  straight to the mountain.
                </p>
              </li>

              <li>
                <strong>Online Marketplaces (Facebook, Craigslist)</strong>
                <p>
                  If you don’t mind meeting private sellers, Facebook Marketplace
                  and Craigslist are goldmines for used skis. Ask for detailed
                  photos, check bindings for safety, and consider meeting at a
                  public place or a local ski shop for inspection. You’ll often
                  snag big-name skis for half of retail or less.
                </p>
              </li>

              <li>
                <strong>Ski Swaps & Community Sales</strong>
                <p
                  ref={(el) => setSpotRef("swap-west-van-arena", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.33968}
                    coordX={-123.15530}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />{" "}
                  <em>Latitude: 49.33968, Longitude: -123.15530</em>
                </p>
                <p>
                  Each fall, events like the Canadian Ski Patrol’s Winter Extreme
                  Swap at venues such as the West Vancouver Arena feature
                  thousands of used skis at bargain prices. Entry is often just a
                  few dollars, and you can find next-to-new gear at 40–60% off.
                  Arrive early for the best selection!
                </p>
              </li>
            </ol>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations" ref={(el) => setSpotRef("local-regulations", el)}>
              Local Regulations &amp; Best Practices
            </h2>
            <p>
              When buying used skis, remember that gear is typically sold “as-is.”
              Many shops won’t service bindings over 10 years old due to safety
              and indemnification rules, so always double-check binding age. 
              In British Columbia, the Business Practices and Consumer Protection
              Act can apply if a store’s item isn’t as described, but private
              sales usually don’t include warranties. 
              <br />
              For safety, it’s crucial to have your bindings tested by a certified
              technician—especially if you buy used. Also, transporting skis must
              comply with BC’s Motor Vehicle Act (properly secured, no excessive
              overhang). If you’re uncertain, consult a local ski shop or the
              Canadian Ski Patrol for guidance.
            </p>

            {/* SECTION: Pricing Guide & Budgeting Tips */}
            <h2 id="pricing-guide" ref={(el) => setSpotRef("pricing-guide", el)}>
              Pricing Guide &amp; Budgeting Tips
            </h2>
            <p>
              <strong>New Skis:</strong> Expect to pay $400–$600 for entry-level,
              $600–$900 for mid-range, and $900+ for premium gear. Watch for
              end-of-season sales (March–April) for the best deals.
            </p>
            <p>
              <strong>Used Skis:</strong> Basic, older models can go as low as
              $100–$200, while nearly-new sets might be $300–$500. Ski swaps and
              online marketplaces can yield big savings if you’re patient. 
            </p>
            <p>
              <strong>Budgeting Tips:</strong> Consider last-year’s models at new
              gear shops for 30–40% off. Ask about package deals (ski + bindings
              + boots) and trade-in programs. If you only ski a few days a year,
              season rentals can sometimes be cheaper overall.
            </p>

            {/* SECTION: Ski Gear Maintenance & Longevity */}
            <h2 id="gear-maintenance" ref={(el) => setSpotRef("gear-maintenance", el)}>
              Ski Gear Maintenance &amp; Longevity
            </h2>
            <p>
              Regular maintenance keeps skis performing their best. Plan to wax
              every 4–6 ski days and sharpen edges to handle Vancouver’s often
              wet or icy conditions. If you spot base gouges, have them repaired
              promptly to prevent further damage. At season’s end, apply a
              “storage wax” and loosen binding DIN settings to reduce spring
              tension. Local shops like Comor, NSSB, and West Coast Sports
              provide tuning services, or you can learn DIY methods with a basic
              kit. 
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. How do I choose the right ski length as a beginner?</h3>
              <p>
                A good rule of thumb is to select skis that reach between your
                chin and nose height. All-mountain skis in the 75–85 mm underfoot
                range are versatile for Vancouver’s mix of conditions.
              </p>

              <h3>2. Can I rent skis for the season?</h3>
              <p>
                Yes. Some shops offer season rentals for around $200–$300. It can
                be cheaper than renting daily if you plan to ski multiple times,
                and you can swap out gear if conditions change.
              </p>

              <h3>3. What should I check when buying used skis from a private seller?</h3>
              <p>
                Inspect the bases for deep gouges, check edges for cracks, and
                verify bindings aren’t too old. If possible, have a shop perform
                a release test before you hit the slopes.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              Navigating Vancouver’s ski marketplace is easier with insider
              knowledge. From well-stocked new gear retailers to budget-friendly
              second-hand shops and community swaps, there’s something for every
              skier’s style and wallet. Remember to inspect used gear carefully,
              budget for essential accessories like bindings and boots, and
              follow local regulations to keep your ski experience safe and
              enjoyable. Now that you’re armed with the best tips and addresses,
              it’s time to find your perfect pair of skis and hit the slopes!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}